import React, { forwardRef, useCallback, useEffect, useRef } from "react";
import { OtpInput } from "reactjs-otp-input";
import { OTPInputProps } from "./OTPInput.types";
import { StyledOTPInputWrapper } from "./OTPInput.styles";
import Typography from "../Typography";
import COLORS from "../../constants/colors";
import { Flex } from "../../layout";
import CrossRedIcon from "../../assets/icon/crossRed.svg";

const OTPInput = forwardRef<HTMLDivElement, OTPInputProps>((props: OTPInputProps, ref) => {
    const {
        onChange,
        value = "",
        id,
        length = 4,
        errorMsg,
        autofocus,
        className,
        inputClassName,
        containerClassName
    } = props;

    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const setWrapperRef = useCallback(
        (node: HTMLDivElement | null) => {
            wrapperRef.current = node;

            if (typeof ref === "function") {
                ref(node);
            } else if (ref) {
                (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
            }
        },
        [ref]
    );

    /**
     * `reactjs-otp-input` advances focus with a bare `.focus()` after every digit, so the
     * browser scrolls the focused box into view on each keystroke. Every box sits on the
     * same row, so that scroll never changes what is visible — it only jerks the screen.
     * Keyboard clearance is handled by the keyboardViewport helpers instead.
     */
    useEffect(() => {
        wrapperRef.current?.querySelectorAll("input").forEach((input: HTMLInputElement) => {
            const focusInput = input.focus.bind(input);

            input.focus = (options?: FocusOptions) => focusInput({ ...options, preventScroll: true });
        });
    }, [length]);

    return (
        <StyledOTPInputWrapper
            ref={setWrapperRef}
            id={id ? `${id}-wrapper` : ""}
            className={className}
            errorMsg={errorMsg}
            $inputClassName={inputClassName}
        >
            <OtpInput
                numInputs={length}
                value={value}
                onChange={onChange}
                isInputNum
                inputStyle={inputClassName ?? "otp-input"}
                containerStyle={containerClassName ?? "otp-wrapper"}
                shouldAutoFocus={autofocus}
                data-testid={id}
            />

            {errorMsg ? (
                <Flex gap={5} alignItems="center">
                    <CrossRedIcon />
                    <Typography color={COLORS.RED} size="s" weight="semibold">
                        {errorMsg}
                    </Typography>
                </Flex>
            ) : null}
        </StyledOTPInputWrapper>
    );
});

OTPInput.displayName = "OTPInput";

export default OTPInput;
